/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Calendar, MapPin } from "lucide-react";
import { publicApi } from "@/lib/api";
import { formatDateLong } from "@/lib/date";

const DISMISS_KEY = "next-event-banner-dismissed";

/**
 * Thin dismissible gold bar shown on Home under the header — surfaces the next
 * upcoming public event (source: /events/next-public). Dismiss state persists
 * per-event so a new event re-shows the banner automatically.
 */
export default function NextEventBanner() {
  const [event, setEvent] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    publicApi.nextEvent()
      .then((d) => {
        if (!d?.event) return;
        setEvent(d.event);
        const stored = localStorage.getItem(DISMISS_KEY);
        if (stored === d.event.id) setDismissed(true);
      })
      .catch(() => { /* fail-soft — no banner is fine */ });
  }, []);

  const onDismiss = () => {
    if (event) localStorage.setItem(DISMISS_KEY, event.id);
    setDismissed(true);
  };

  if (!event || dismissed) return null;

  return (
    <div
      data-testid="next-event-banner"
      className="relative bg-gradient-to-r from-[#DCB457]/95 via-[#e8c568] to-[#DCB457]/95 text-black overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-2 md:py-2.5 flex items-center justify-between gap-4">
        <Link
          to={`/billetterie/${event.slug}`}
          data-testid="next-event-banner-link"
          className="flex items-center gap-3 md:gap-5 flex-1 min-w-0 group"
        >
          <span className="hidden md:inline text-[10px] uppercase tracking-[0.24em] font-medium opacity-80 shrink-0">
            🎷 Prochain événement
          </span>
          <span className="font-serif-luxe text-sm md:text-base truncate">
            {event.title}
          </span>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[12px] opacity-85 shrink-0">
            <Calendar size={12} strokeWidth={1.5} />
            {formatDateLong(event.date)}
          </span>
          {event.location && (
            <span className="hidden lg:inline-flex items-center gap-1.5 text-[12px] opacity-85 shrink-0">
              <MapPin size={12} strokeWidth={1.5} /> {event.location}
            </span>
          )}
          <span className="ml-auto text-[11px] uppercase tracking-[0.18em] font-medium underline-offset-2 group-hover:underline shrink-0">
            Réserver →
          </span>
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="next-event-banner-close"
          className="opacity-60 hover:opacity-100 transition shrink-0"
          aria-label="Fermer"
        >
          <X size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}


/**
 * Discreet compact block for the footer — same data source, always visible.
 */
export function NextEventFooterBlock() {
  const [event, setEvent] = useState(null);
  useEffect(() => {
    publicApi.nextEvent().then((d) => d?.event && setEvent(d.event)).catch(() => {});
  }, []);
  if (!event) return null;
  return (
    <Link
      to={`/billetterie/${event.slug}`}
      data-testid="next-event-footer-block"
      className="block mb-6 px-4 py-3 border border-[#DCB457]/40 hover:border-[#DCB457] hover:bg-[#DCB457]/[0.05] transition group"
    >
      <p className="text-[9px] uppercase tracking-[0.28em] text-[#DCB457] mb-1">🎷 Prochain rendez-vous</p>
      <p className="font-serif-luxe text-base group-hover:text-[#DCB457] transition truncate">{event.title}</p>
      <p className="text-[11px] text-white/60 mt-1">
        {formatDateLong(event.date)}{event.location ? ` · ${event.location}` : ""}
      </p>
    </Link>
  );
}
